"use client";

import { c as _c } from "react/compiler-runtime";
import React, { type JSX, useEffect, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useSetWaveData } from "@/contexts/TitleContext";
import { useContentTab } from "../ContentTabContext";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import MyStreamWaveChat from "./MyStreamWaveChat";
import MyStreamWaveCurationContent from "./curations/MyStreamWaveCurationContent";
import { useWaveData } from "@/hooks/useWaveData";
import MyStreamWaveLeaderboard from "./MyStreamWaveLeaderboard";
import MyStreamWaveOutcome from "./MyStreamWaveOutcome";
import { useSearchParams, usePathname, useRouter } from "next/navigation";
import { WaveWinners } from "@/components/waves/winners/WaveWinners";
import { MyStreamWaveTab } from "@/types/waves.types";
import { MyStreamWaveTabs } from "./tabs/MyStreamWaveTabs";
import MyStreamWaveMyVotes from "./votes/MyStreamWaveMyVotes";
import MyStreamWaveFAQ from "./MyStreamWaveFAQ";
import MyStreamWaveSales from "./MyStreamWaveSales";
import { useMyStream } from "@/contexts/wave/MyStreamContext";
import { createBreakpoint } from "react-use";
import { getHomeRoute, getWaveHomeRoute } from "@/helpers/navigation.helpers";
import { useWaveViewMode } from "@/hooks/useWaveViewMode";
import { useWave } from "@/hooks/useWave";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
import useDeviceInfo from "@/hooks/useDeviceInfo";
interface MyStreamWaveProps {
  readonly waveId: string;
}
const getContentTabPanelId = (tab: MyStreamWaveTab): string => `my-stream-wave-tabpanel-${tab.toLowerCase()}`;
const useBreakpoint = createBreakpoint({
  LG: 1024,
  S: 0
});
const MyStreamWave: React.FC<MyStreamWaveProps> = t0 => {
  const $ = _c(89);
  const {
    waveId
  } = t0;
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();
  const {
    isApp
  } = useDeviceInfo();
  const queryClient = useQueryClient();
  const {
    waves,
    directMessages
  } = useMyStream();
  let t1;
  if ($[0] !== isApp || $[1] !== pathname || $[2] !== router || $[3] !== searchParams) {
    t1 = () => {
      const params = new URLSearchParams(searchParams.toString() || "");
      params.delete("wave");
      const basePath = getWaveHomeRoute({
        isDirectMessage: pathname.startsWith("/messages"),
        isApp
      });
      const newUrl = params.toString() ? `${basePath}?${params.toString()}` : basePath || getHomeRoute();
      router.push(newUrl, {
        scroll: false
      });
    };
    $[0] = isApp;
    $[1] = pathname;
    $[2] = router;
    $[3] = searchParams;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== t1 || $[6] !== waveId) {
    t2 = {
      waveId,
      onWaveNotFound: t1
    };
    $[5] = t1;
    $[6] = waveId;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const {
    data: wave
  } = useWaveData(t2);
  let t3;
  if ($[8] !== directMessages.list || $[9] !== waveId || $[10] !== waves.list) {
    t3 = waves.list.find(w_0 => w_0.id === waveId) ?? directMessages.list.find(w => w.id === waveId);
    $[8] = directMessages.list;
    $[9] = waveId;
    $[10] = waves.list;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const waveFromList = t3;
  const t4 = waveFromList?.newDropsCount.count ?? 0;
  const t5 = waveFromList?.firstUnreadDropSerialNo ?? null;
  let t6;
  if ($[12] !== t4 || $[13] !== t5) {
    t6 = {
      newDropsCount: t4,
      firstUnreadSerialNo: t5
    };
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const enhancedData = t6;
  const newDropsCount = enhancedData.newDropsCount;
  let t7;
  if ($[15] !== newDropsCount || $[16] !== wave) {
    t7 = wave ? {
      name: wave.name,
      newItemsCount: newDropsCount
    } : null;
    $[15] = newDropsCount;
    $[16] = wave;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  useSetWaveData(t7);
  const stableWaveKey = `wave-${waveId}`;
  const {
    activeContentTab
  } = useContentTab();
  let t8;
  if ($[18] !== searchParams) {
    t8 = searchParams.get("curation");
    $[18] = searchParams;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  const activeCurationId = t8;
  const {
    viewMode,
    setViewMode,
    toggleViewMode
  } = useWaveViewMode(waveId);
  const {
    isRankWave,
    isMemesWave,
    isDm
  } = useWave(wave);
  const showGalleryToggle = !isRankWave && !isMemesWave && !isDm;
  let t9;
  if ($[20] !== searchParams) {
    t9 = searchParams.get("serialNo");
    $[20] = searchParams;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  const hasSerialTarget = t9 !== null;
  let t10;
  let t11;
  if ($[22] !== hasSerialTarget || $[23] !== setViewMode || $[24] !== showGalleryToggle || $[25] !== viewMode || $[26] !== wave) {
    t10 = () => {
      if (!wave || !hasSerialTarget || !showGalleryToggle || viewMode !== "gallery") {
        return;
      }
      setViewMode("chat");
    };
    t11 = [hasSerialTarget, setViewMode, showGalleryToggle, viewMode, wave];
    $[22] = hasSerialTarget;
    $[23] = setViewMode;
    $[24] = showGalleryToggle;
    $[25] = viewMode;
    $[26] = wave;
    $[27] = t10;
    $[28] = t11;
  } else {
    t10 = $[27];
    t11 = $[28];
  }
  useEffect(t10, t11);
  useBreakpoint();
  let t12;
  if ($[29] !== pathname || $[30] !== queryClient || $[31] !== router || $[32] !== searchParams) {
    t12 = drop => {
      queryClient.setQueryData([QueryKey.DROP, {
        drop_id: drop.id
      }], drop as ApiDrop);
      const params_0 = new URLSearchParams(searchParams.toString() || "");
      params_0.set("drop", drop.id);
      router.push(`${pathname}?${params_0.toString()}`, {
        scroll: false
      });
    };
    $[29] = pathname;
    $[30] = queryClient;
    $[31] = router;
    $[32] = searchParams;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  const onDropClick = t12;
  let t13;
  if ($[34] !== pathname || $[35] !== router || $[36] !== searchParams) {
    t13 = curationId => {
      const params_1 = new URLSearchParams(searchParams.toString() || "");
      if (curationId) {
        params_1.set("curation", curationId);
      } else {
        params_1.delete("curation");
      }
      const nextQuery = params_1.toString();
      const nextUrl = nextQuery ? `${pathname}?${nextQuery}` : pathname;
      router.replace(nextUrl, {
        scroll: false
      });
    };
    $[34] = pathname;
    $[35] = router;
    $[36] = searchParams;
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  const onSelectCuration = t13;
  if (!wave) {
    return null;
  }
  const t14 = showGalleryToggle ? viewMode : "chat";
  let t15;
  if ($[38] !== enhancedData.firstUnreadSerialNo || $[39] !== onDropClick || $[40] !== t14 || $[41] !== wave) {
    t15 = <MyStreamWaveChat wave={wave} firstUnreadSerialNo={enhancedData.firstUnreadSerialNo} viewMode={t14} onDropClick={onDropClick} />;
    $[38] = enhancedData.firstUnreadSerialNo;
    $[39] = onDropClick;
    $[40] = t14;
    $[41] = wave;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  let t16;
  if ($[43] !== onDropClick || $[44] !== wave) {
    t16 = <MyStreamWaveLeaderboard key={wave.id} wave={wave} onDropClick={onDropClick} />;
    $[43] = onDropClick;
    $[44] = wave;
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  let t17;
  if ($[46] !== wave.id) {
    t17 = <MyStreamWaveSales waveId={wave.id} />;
    $[46] = wave.id;
    $[47] = t17;
  } else {
    t17 = $[47];
  }
  let t18;
  if ($[48] !== onDropClick || $[49] !== wave) {
    t18 = <WaveWinners wave={wave} onDropClick={onDropClick} />;
    $[48] = onDropClick;
    $[49] = wave;
    $[50] = t18;
  } else {
    t18 = $[50];
  }
  let t19;
  if ($[51] !== wave) {
    t19 = <MyStreamWaveOutcome wave={wave} />;
    $[51] = wave;
    $[52] = t19;
  } else {
    t19 = $[52];
  }
  let t20;
  if ($[53] !== onDropClick || $[54] !== wave) {
    t20 = <MyStreamWaveMyVotes wave={wave} onDropClick={onDropClick} />;
    $[53] = onDropClick;
    $[54] = wave;
    $[55] = t20;
  } else {
    t20 = $[55];
  }
  let t21;
  if ($[56] !== wave) {
    t21 = <MyStreamWaveFAQ wave={wave} />;
    $[56] = wave;
    $[57] = t21;
  } else {
    t21 = $[57];
  }
  let t22;
  if ($[58] !== t15 || $[59] !== t16 || $[60] !== t17 || $[61] !== t18 || $[62] !== t19 || $[63] !== t20 || $[64] !== t21) {
    t22 = {
      [MyStreamWaveTab.CHAT]: t15,
      [MyStreamWaveTab.LEADERBOARD]: t16,
      [MyStreamWaveTab.SALES]: t17,
      [MyStreamWaveTab.WINNERS]: t18,
      [MyStreamWaveTab.OUTCOME]: t19,
      [MyStreamWaveTab.MY_VOTES]: t20,
      [MyStreamWaveTab.FAQ]: t21
    };
    $[58] = t15;
    $[59] = t16;
    $[60] = t17;
    $[61] = t18;
    $[62] = t19;
    $[63] = t20;
    $[64] = t21;
    $[65] = t22;
  } else {
    t22 = $[65];
  }
  const components = t22;
  let t23;
  if ($[66] !== activeCurationId || $[67] !== onSelectCuration || $[68] !== showGalleryToggle || $[69] !== toggleViewMode || $[70] !== viewMode || $[71] !== wave) {
    t23 = <MyStreamWaveTabs wave={wave} viewMode={viewMode} onToggleViewMode={toggleViewMode} showGalleryToggle={showGalleryToggle} activeCurationId={activeCurationId} onSelectCuration={onSelectCuration} />;
    $[66] = activeCurationId;
    $[67] = onSelectCuration;
    $[68] = showGalleryToggle;
    $[69] = toggleViewMode;
    $[70] = viewMode;
    $[71] = wave;
    $[72] = t23;
  } else {
    t23 = $[72];
  }
  let t24;
  if ($[73] !== activeContentTab || $[74] !== activeCurationId) {
    t24 = activeCurationId ? `my-stream-wave-tabpanel-curation-${activeCurationId}` : getContentTabPanelId(activeContentTab);
    $[73] = activeContentTab;
    $[74] = activeCurationId;
    $[75] = t24;
  } else {
    t24 = $[75];
  }
  let t25;
  if ($[76] !== activeContentTab || $[77] !== activeCurationId || $[78] !== components || $[79] !== onDropClick || $[80] !== wave) {
    t25 = activeCurationId ? <MyStreamWaveCurationContent key={activeCurationId} wave={wave} curationId={activeCurationId} onDropClick={onDropClick} /> : components[activeContentTab];
    $[76] = activeContentTab;
    $[77] = activeCurationId;
    $[78] = components;
    $[79] = onDropClick;
    $[80] = wave;
    $[81] = t25;
  } else {
    t25 = $[81];
  }
  let t26;
  if ($[82] !== t24 || $[83] !== t25) {
    t26 = <div className="tw-relative tw-min-w-0 tw-flex-grow tw-overflow-hidden" role="tabpanel" id={t24}>{t25}</div>;
    $[82] = t24;
    $[83] = t25;
    $[84] = t26;
  } else {
    t26 = $[84];
  }
  let t27;
  if ($[85] !== stableWaveKey || $[86] !== t23 || $[87] !== t26) {
    t27 = <div className="tailwind-scope tw-relative tw-flex tw-h-full tw-min-w-0 tw-flex-col" key={stableWaveKey}>{t23}{t26}</div>;
    $[85] = stableWaveKey;
    $[86] = t23;
    $[87] = t26;
    $[88] = t27;
  } else {
    t27 = $[88];
  }
  return t27;
};
export default React.memo(MyStreamWave);